export const languages = ["python", "node", "http"] as const;
export type Language = (typeof languages)[number];
export const modes = [
  "faceSwap",
  "videoToVideo",
  "imageToVideo",
  "textToVideo",
  "animation",
] as const;
export type Mode = (typeof modes)[number];

type ApiShowcaseConfig = {
  [mode in Mode]: {
    mediaKey: string;
    codeSnippet: {
      [lang in Language]: string;
    };
  };
};

const envName = "MAGIC_HOUR_API_KEY";
const pythonShared = `
from magic_hour import Client
from os import getenv
client = Client(token=getenv("${envName}"))
`;
const nodeShared = `
import Client from "magic-hour";
const token = process.env.${envName}!;
const client = new Client({ token });
`;

const modeData = {
  shared: {
    startSeconds: 0,
    endSeconds: 15,
    height: 720,
    width: 1280,
    audioSource: "file",
    audioFilePath: "audio/id/song.mp3",
    videoSource: "file",
    videoFilePath: "video/id/movie.mp4",
    imageFilePath: "image/id/photo.png",
  },
  animation: {
    fps: 12,
    artStyle: "Oil Painting",
    cameraEffect: "Simple Zoom In",
    prompt:
      "Beautiful landscape nature lakes valleys mountains, bokeh shallow-depth-of-field horizon sunset clouds dynamicrange lighting beautiful blue sky",
    promptType: "custom",
    transitionSpeed: 5,
  },
  imageToVideo: {
    prompt: "waves crashing",
  },
  textToVideo: {
    prompt:
      "FPV moving through a forest to san francisco skyline, constantly changing, blurry, rapid",
  },
  videoToVideo: {
    artStyle: "Celestial Skin",
    promptType: "default",
    model: "default",
    version: "default",
  },
};

export const apiShowcaseConfig: ApiShowcaseConfig = {
  animation: {
    mediaKey: `animation`,
    codeSnippet: {
      python: `${pythonShared}
res = client.v1.animation.create(
  style={
    "art_style": "${modeData.animation.artStyle}",
    "camera_effect": "${modeData.animation.cameraEffect}",
    "prompt": "${modeData.animation.prompt}",
    "prompt_type": "${modeData.animation.promptType}",
    "transition_speed": ${modeData.animation.transitionSpeed},
  },
  assets={
    "audio_source": "${modeData.shared.audioSource}",
    "audio_file_path": "${modeData.shared.audioFilePath}",
  },
  fps=${modeData.animation.fps},
  end_seconds=${modeData.shared.endSeconds},
  height=${modeData.shared.height},
  width=${modeData.shared.width},
)`,
      node: `${nodeShared}
const res = await client.v1.animation.create({
  style: {
    artStyle: "${modeData.animation.artStyle}",
    cameraEffect: "${modeData.animation.cameraEffect}",
    prompt: "${modeData.animation.prompt}",
    promptType: "${modeData.animation.promptType}",
    transitionSpeed: ${modeData.animation.transitionSpeed},
  },
  assets: { 
    audioSource: "${modeData.shared.audioSource}",
    audioFilePath: "${modeData.shared.audioFilePath}"
  },
  fps: ${modeData.animation.fps},
  endSeconds: ${modeData.shared.endSeconds},
  height: ${modeData.shared.height},
  width: ${modeData.shared.width},
});
`,
      http: `
curl https://api.magichour.ai/v1/animation \\
  --request POST \\
  --header 'Authorization: Bearer $${envName}' \\
  --header 'Content-Type: application/json' \\
  --data '{
  "style": {
    "art_style": "${modeData.animation.artStyle}",
    "camera_effect": "${modeData.animation.cameraEffect}",
    "prompt": "${modeData.animation.prompt}",
    "prompt_type": "${modeData.animation.promptType}",
    "transition_speed": ${modeData.animation.transitionSpeed}
  },
  "assets": {
    "audio_source": "${modeData.shared.audioSource}",
    "audio_file_path": "${modeData.shared.audioFilePath}"
  },
  "fps": ${modeData.animation.fps},
  "end_seconds": ${modeData.shared.endSeconds},
  "height": ${modeData.shared.height},
  "width": ${modeData.shared.width}
}'
`,
    },
  },
  faceSwap: {
    mediaKey: `face-swap`,
    codeSnippet: {
      python: `${pythonShared}
res = client.v1.face_swap.create(
  assets={
    "image_file_path": "${modeData.shared.imageFilePath}", 
    "video_source": "${modeData.shared.videoSource}",
    "video_file_path": "${modeData.shared.videoFilePath}",
  },
  start_seconds: ${modeData.shared.startSeconds},
  end_seconds: ${modeData.shared.endSeconds},
  height: ${modeData.shared.height},
  width: ${modeData.shared.width},
)`,
      node: `${nodeShared}
const res = await client.v1.faceSwap.create({
  assets: { 
    imageFilePath: "${modeData.shared.imageFilePath}",
    videoSource: "${modeData.shared.videoSource}",
    videoFilePath: "${modeData.shared.videoFilePath}",
  },
  startSeconds: ${modeData.shared.startSeconds},
  endSeconds: ${modeData.shared.endSeconds},
  height: ${modeData.shared.height},
  width: ${modeData.shared.width},
});`,
      http: `
curl https://api.magichour.ai/v1/face-swap --request POST \\
  --header "Authorization: Bearer $${envName}" \\
  --header "Content-Type: application/json" \\
  --data '{
    "assets": {
      "image_file_path": "${modeData.shared.imageFilePath}", 
      "video_source": "${modeData.shared.videoSource}",
      "video_file_path": "${modeData.shared.videoFilePath}"
    },
    "start_seconds": ${modeData.shared.startSeconds},
    "end_seconds": ${modeData.shared.endSeconds},
    "height": ${modeData.shared.height},
    "width": ${modeData.shared.width}
  }'
`,
    },
  },
  imageToVideo: {
    mediaKey: `image-to-video`,
    codeSnippet: {
      python: `${pythonShared}
res = client.v1.image_to_video.create(
  style={
    "prompt": "${modeData.imageToVideo.prompt}",
  },
  assets={
    "image_file_path": "${modeData.shared.imageFilePath}",
  },
  end_seconds: ${modeData.shared.endSeconds},
  height: ${modeData.shared.height},
  width: ${modeData.shared.width},
)`,
      node: `${nodeShared}
const res = await client.v1.imageToVideo.create({
  style: {
    prompt: "${modeData.imageToVideo.prompt}",
  },
  assets: {
    imageFilePath: "${modeData.shared.imageFilePath}",
  },
  endSeconds: ${modeData.shared.endSeconds},
  height: ${modeData.shared.height},
  width: ${modeData.shared.width},
});`,
      http: `
curl https://api.magichour.ai/v1/image-to-video --request POST \\
  --header 'Authorization: Bearer $${envName}' \\
  --header 'Content-Type: application/json' \\
  --data '{
  "prompt": {
    "prompt": "${modeData.imageToVideo.prompt}"
  },
  "assets": {
    "image_file_path": "${modeData.shared.imageFilePath}"
  },
  "end_seconds": ${modeData.shared.endSeconds},
  "height": ${modeData.shared.height},
  "width": ${modeData.shared.width}
}'
`,
    },
  },
  textToVideo: {
    mediaKey: `text-to-video`,
    codeSnippet: {
      python: `${pythonShared}
res = client.v1.text_to_video.create(
  style={
    "prompt": "${modeData.textToVideo.prompt}",
  },
  end_seconds: ${modeData.shared.endSeconds},
  height: ${modeData.shared.height},
  width: ${modeData.shared.width},
)`,
      node: `${nodeShared}
const res = await client.v1.textToVideo.create({
  style: {
    prompt: "${modeData.textToVideo.prompt}",
  },
  endSeconds: ${modeData.shared.endSeconds},
  height: ${modeData.shared.height},
  width: ${modeData.shared.width},
});`,
      http: `
curl https://api.magichour.ai/v1/text-to-video --request POST \\
  --header 'Authorization: Bearer $${envName}' \\
  --header 'Content-Type: application/json' \\
  --data '{
  "prompt": {
    "prompt": "${modeData.textToVideo.prompt}"
  },
  "end_seconds": ${modeData.shared.endSeconds},
  "height": ${modeData.shared.height},
  "width": ${modeData.shared.width}
}'
`,
    },
  },
  videoToVideo: {
    mediaKey: `video-to-video`,
    codeSnippet: {
      python: `${pythonShared}
res = client.v1.video_to_video.create(
  style={
    "art_style": "${modeData.videoToVideo.artStyle}",
    "model": "${modeData.videoToVideo.model}",
    "prompt": None,
    "prompt_type": "${modeData.videoToVideo.promptType}",
    "version": "${modeData.videoToVideo.version}",
  },
  assets={
    "video_source": "${modeData.shared.videoSource}",
    "video_file_path": "${modeData.shared.videoFilePath}",
  },
  start_seconds: ${modeData.shared.startSeconds},
  end_seconds: ${modeData.shared.endSeconds},
  height: ${modeData.shared.height},
  width: ${modeData.shared.width},
)`,
      node: `${nodeShared}
const res = await client.v1.videoToVideo.create({
  style: {
    artStyle: "${modeData.videoToVideo.artStyle}",
    model: "${modeData.videoToVideo.model}",
    prompt: null,
    prompt_type: "${modeData.videoToVideo.promptType}",
    version: "${modeData.videoToVideo.version}",
  },
  assets: { 
    videoSource: "${modeData.shared.videoSource}",
    videoFilePath: "${modeData.shared.videoFilePath}",
  },
  startSeconds: ${modeData.shared.startSeconds},
  endSeconds: ${modeData.shared.endSeconds},
  height: ${modeData.shared.height},
  width: ${modeData.shared.width},
});`,
      http: `
curl https://api.magichour.ai/v1/video-to-video --request POST \\
  --header 'Authorization: Bearer $${envName}' \\
  --header 'Content-Type: application/json' \\
  --data '{
  "style": {
    "art_style": "${modeData.videoToVideo.artStyle}",
    "model": "${modeData.videoToVideo.model}",
    "prompt": null,
    "prompt_type": "${modeData.videoToVideo.promptType}",
    "version": "${modeData.videoToVideo.version}"
  },
  "assets": {
    "video_source": "${modeData.shared.videoSource}",
    "video_file_path": "${modeData.shared.videoFilePath}"
  },
  "start_seconds": ${modeData.shared.startSeconds},
  "end_seconds": ${modeData.shared.endSeconds},
  "height": ${modeData.shared.height},
  "width": ${modeData.shared.width}
}'
`,
    },
  },
};
